import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router';
import { debounce } from 'lodash';
import {
  Box,
  List,
  ListSubheader,
  Typography,
  CircularProgress,
} from '@mui/material';
import PerfectScrollbarTable from 'src/components/PerfectScrollbarTable';
import ChatListFilter from 'src/views/management/ChatView/ChatSidebar/ChatList/ChatsListFilter';
import ChatItem from 'src/views/management/ChatView/ChatSidebar/ChatList/ChatItem';
import ChatRefresh from 'src/views/management/ChatView/ChatSidebar/ChatList/ChatRefresh';
import ShortCodesChatItem from 'src/views/management/ChatView/ShortCodes/ShortCodesChatItem';
import { chatOrderEntry, shortCodesRowIndex } from 'src/views/management/ChatView/ShortCodes/shortCodePlacement';
import { getScrollTopToRevealRow } from 'src/views/management/ChatView/ChatSidebar/ChatList/listScrollRestore';
import { clearDeletedChatKey, getMoreChats, getChatsList } from 'src/actions/chatActions';
import helpers from 'src/helpers';
import { FILTER_TYPES } from 'src/constants/chat';
import { ROUTES } from 'src/constants';

function ChatList({ searchText, onClearSearch, isHidden = false }) {
  const [selectedFilterOption, setSelectedFilterOption] = useState(FILTER_TYPES.all);
  const [isSearching, setIsSearching] = useState(false);
  const { chatKey: chatKeyUrl } = useParams();
  const loadMoreRef = useRef(null);
  const observerRef = useRef(null);
  const previousSearchRef = useRef('');
  const listBoxRef = useRef(null);
  const savedScrollTopRef = useRef(0);
  const visitedChatKeyRef = useRef(null);
  const wasHiddenRef = useRef(isHidden);
  const history = useHistory();

  const {
    chats,
    chatsContacts,
    isChatsLoading,
    isChatsLoadingMore,
    pagination,
  } = useSelector((state) => state?.chat || {});
  const dispatch = useDispatch();

  const {
    hasMore = false,
    currentPage = 1,
    totalChats = 0,
    totalPages = 1,
  } = pagination || {};

  const filterOptions = [
    { value: FILTER_TYPES.all, label: 'All Chats' },
    { value: FILTER_TYPES.unread, label: 'Unread' },
    { value: FILTER_TYPES.sent, label: 'Sent' },
    { value: FILTER_TYPES.scheduled, label: 'Scheduled' },
    { value: FILTER_TYPES.drafts, label: 'Drafts' },
    { isDivider: true, id: 'divider-1' },
    { value: FILTER_TYPES.flagRed, label: 'Red Flag' },
    { value: FILTER_TYPES.flagOrange, label: 'Orange Flag' },
    { value: FILTER_TYPES.flagYellow, label: 'Yellow Flag' },
    { value: FILTER_TYPES.flagGreen, label: 'Green Flag' },
    { value: FILTER_TYPES.flagBlue, label: 'Blue Flag' },
    { isDivider: true, id: 'divider-2' },
    { value: FILTER_TYPES.archived, label: 'Archived' },
  ];

  // Helper function to get filter options for API calls based on selected filter
  const getFilterOptionsForApi = useCallback((filterValue) => {
    const baseOptions = { type: filterValue };

    // Handle flag color filters
    if (filterValue === FILTER_TYPES.flagRed) {
      return { ...baseOptions, flagColor: 'red' };
    }
    if (filterValue === FILTER_TYPES.flagOrange) {
      return { ...baseOptions, flagColor: 'orange' };
    }
    if (filterValue === FILTER_TYPES.flagYellow) {
      return { ...baseOptions, flagColor: 'yellow' };
    }
    if (filterValue === FILTER_TYPES.flagGreen) {
      return { ...baseOptions, flagColor: 'green' };
    }
    if (filterValue === FILTER_TYPES.flagBlue) {
      return { ...baseOptions, flagColor: 'blue' };
    }

    // Handle archived filter
    if (filterValue === FILTER_TYPES.archived) {
      return { ...baseOptions, showArchived: true };
    }

    // Handle drafts filter (chats with a saved compose draft, incl. AI drafts)
    if (filterValue === FILTER_TYPES.drafts) {
      return { ...baseOptions, showDrafts: true };
    }

    return baseOptions;
  }, []);

  // ============================================
  // Backend Search with Debounce
  // ============================================
  const handleBackendSearch = useCallback(
    debounce((searchValue) => {
      if (searchValue !== previousSearchRef.current) {
        previousSearchRef.current = searchValue;
        setIsSearching(true);
        dispatch(getChatsList(
          true,
          searchValue,
          getFilterOptionsForApi(selectedFilterOption)
        ))
          .finally(() => setIsSearching(false));
      }
    }, 500),
    [dispatch, selectedFilterOption, getFilterOptionsForApi]
  );

  // ============================================
  // Frontend Filter (for Unread only)
  // ============================================
  // It makes sense to leave the next lines if we want to remove contact after reading the message
  const filteredChats = useMemo(() => {
    let filtered = [...chats.allKeys];

    if (selectedFilterOption === FILTER_TYPES.unread) {
      filtered = filtered.filter((chatKey) => {
        const chat = chats.byKey[chatKey];
        // to show unread + current opened
        return chat.unreadCount > 0 || chatKeyUrl === chatKey;
      });
    }

    return filtered;
  }, [chats, selectedFilterOption, chatKeyUrl]);

  const sortedChats = useMemo(
    () => helpers.chat.sortChatsBySocketDateReceived(chats, filteredChats, chatsContacts),
    [chats, filteredChats, chatsContacts]
  );

  const [totalUnread, realUnreadMessagesCount] = useMemo(() => {
    const resultTotal = chats?.allKeys?.length || 0;

    if (
      selectedFilterOption === FILTER_TYPES.unread
      && chats?.allKeys?.length
    ) {
      const resultUnread = (chats.allKeys.filter((chatKey) => chats.byKey?.[chatKey]?.unreadCount > 0)?.length || 0);
      const resultTotalUnread = resultTotal - (chats.allKeys.filter((chatKey) => !chats.byKey?.[chatKey]?.unreadCount && chatKey !== chatKeyUrl)?.length || 0);

      return [
        resultTotalUnread > 0 ? resultTotalUnread : 0,
        resultUnread,
      ];
    }

    return [
      resultTotal,
      0, // Not calculated for other filter types yet
    ];
  }, [chats, selectedFilterOption, chatKeyUrl]);

  const handleClick = useCallback(() => {
    dispatch(clearDeletedChatKey());
  }, [dispatch]);

  // ============================================
  // Lazy Loading Logic
  // ============================================
  const loadMore = useCallback(() => {
    const shouldLoad = !isChatsLoading && !isChatsLoadingMore && hasMore && !isSearching && currentPage < totalPages;

    if (shouldLoad) {
      dispatch(getMoreChats(
        currentPage + 1,
        searchText,
        getFilterOptionsForApi(selectedFilterOption)
      ));
    }
  }, [
    dispatch,
    isChatsLoading,
    isChatsLoadingMore,
    hasMore,
    currentPage,
    selectedFilterOption,
    isSearching,
    totalPages,
    searchText,
    getFilterOptionsForApi
  ]);

  // Helper to check if the currently selected chat matches the new filter
  const doesSelectedChatMatchFilter = useCallback((filterValue) => {
    if (!chatKeyUrl) return true; // No chat selected, nothing to check

    // Get the current chat and its contact
    const currentChat = chats.byKey?.[chatKeyUrl];
    const contactId = currentChat?.participantIds?.[0];
    const contact = contactId ? chatsContacts.byId?.[String(contactId)] : null;
    const chatPreferences = contact?.chatPreferences || {};
    const { flagColor = 'none', isArchived = false } = chatPreferences;
    const unreadCount = currentChat?.unreadCount || 0;

    // Check if chat matches the new filter
    switch (filterValue) {
      case FILTER_TYPES.all:
        // "All" filter excludes archived chats
        return !isArchived;

      case FILTER_TYPES.unread:
        return unreadCount > 0;

      case FILTER_TYPES.flagRed:
        return flagColor === 'red' && !isArchived;

      case FILTER_TYPES.flagOrange:
        return flagColor === 'orange' && !isArchived;

      case FILTER_TYPES.flagYellow:
        return flagColor === 'yellow' && !isArchived;

      case FILTER_TYPES.flagGreen:
        return flagColor === 'green' && !isArchived;

      case FILTER_TYPES.flagBlue:
        return flagColor === 'blue' && !isArchived;

      case FILTER_TYPES.archived:
        return isArchived;

      case FILTER_TYPES.sent:
      case FILTER_TYPES.scheduled:
        // These filters require backend data to determine if chat matches
        // Always navigate away and let the backend determine the list
        return false;

      default:
        return true;
    }
  }, [chatKeyUrl, chats.byKey, chatsContacts.byId]);

  const handleChatListFilterChange = useCallback((event) => {
    const { value } = event?.target || {};
    setSelectedFilterOption(value);

    // Only navigate away if the selected chat doesn't match the new filter
    if (chatKeyUrl && !doesSelectedChatMatchFilter(value)) {
      history.push(ROUTES.APP_MANAGEMENT_CHAT);
    }

    dispatch(getChatsList(
      true,
      searchText,
      getFilterOptionsForApi(value)
    ));
  }, [dispatch, setSelectedFilterOption, searchText, getFilterOptionsForApi, chatKeyUrl, history, doesSelectedChatMatchFilter]);

  // useEffect Should Always Be Placed Before the return Statement or any JSX variables
  useEffect(() => {
    handleBackendSearch(searchText);
  }, [searchText, handleBackendSearch]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '200px',
      threshold: 0.1,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        loadMore();
      }
    }, options);

    const currentLoadMoreRef = loadMoreRef.current;
    if (currentLoadMoreRef) {
      observerRef.current.observe(currentLoadMoreRef);
    }

    return () => {
      if (observerRef.current && currentLoadMoreRef) {
        observerRef.current.unobserve(currentLoadMoreRef);
      }
    };
  }, [isChatsLoading, isChatsLoadingMore, hasMore, isSearching]);

  // ============================================
  // CON-1754 Scroll position across a phone round trip
  // ============================================
  // The scroller is the PerfectScrollbar container this list renders into.
  // Its offset is recorded as the user scrolls, because once the list is
  // hidden the browser reports 0 for a display: none element.
  useEffect(() => {
    const scroller = listBoxRef.current?.parentElement;
    if (!scroller) return undefined;

    const recordScrollTop = () => {
      if (scroller.clientHeight > 0) {
        savedScrollTopRef.current = scroller.scrollTop;
      }
    };

    scroller.addEventListener('scroll', recordScrollTop, { passive: true });
    return () => scroller.removeEventListener('scroll', recordScrollTop);
  }, []);

  useEffect(() => {
    if (isHidden && chatKeyUrl) {
      visitedChatKeyRef.current = chatKeyUrl;
    }
  }, [isHidden, chatKeyUrl]);

  // Layout effect so the list does not paint at the top first. Restores the
  // offset, then brings the visited conversation into view only when its row
  // is still in the list and entirely off screen. Under Unread the row is
  // gone once read, so the offset alone applies, as on desktop.
  useLayoutEffect(() => {
    const wasHidden = wasHiddenRef.current;
    wasHiddenRef.current = isHidden;
    const listBox = listBoxRef.current;
    const scroller = listBox?.parentElement;
    if (!wasHidden || isHidden || !scroller) return;

    scroller.scrollTop = savedScrollTopRef.current;

    const visitedChatKey = visitedChatKeyRef.current;
    visitedChatKeyRef.current = null;
    const row = visitedChatKey
      ? Array.from(listBox.querySelectorAll('[data-chat-key]'))
        .find((element) => element.dataset.chatKey === String(visitedChatKey))
      : null;

    if (row) {
      const scrollerRect = scroller.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      const revealScrollTop = getScrollTopToRevealRow({
        scrollTop: scroller.scrollTop,
        containerTop: scrollerRect.top,
        containerBottom: scrollerRect.bottom,
        rowTop: rowRect.top,
        rowBottom: rowRect.bottom,
        topInset: listBox.querySelector('.MuiListSubheader-root')?.offsetHeight || 0,
      });
      if (revealScrollTop !== null) {
        scroller.scrollTop = revealScrollTop;
      }
    }

    // PerfectScrollbar redraws its rail on scroll. Setting an unchanged
    // scrollTop fires no event, and any update it ran while hidden measured
    // an empty box.
    scroller.dispatchEvent(new Event('scroll'));
  }, [isHidden]);

  const currentFilterOptions = useMemo(
    () => getFilterOptionsForApi(selectedFilterOption),
    [selectedFilterOption, getFilterOptionsForApi]
  );

  // CON-2014 — the one Short codes row shows under All Chats, and under
  // Unread while it has unread texts. Not under Sent, Scheduled, Drafts, a
  // flag or Archived, and not while searching.
  const {
    shortCodes: shortCodeList = [],
    totalUnreadCount: shortCodeUnreadCount = 0,
  } = useSelector((state) => state?.shortCodes || {});
  const showShortCodesItem = shortCodeList.length > 0
    && !searchText
    && (selectedFilterOption === FILTER_TYPES.all
      || (selectedFilterOption === FILTER_TYPES.unread && shortCodeUnreadCount > 0));

  // CON-2034 — placed where the server's order puts a chat with its latest
  // text (shortCodePlacement.js); -1 leaves it out until paging reaches it.
  const shortCodesIndex = useMemo(() => {
    if (!showShortCodesItem) return -1;
    const entries = sortedChats.map((chatKey) => {
      const chat = chats.byKey[chatKey];
      const contactId = chat?.participantIds?.[0];
      const contact = contactId ? chatsContacts?.byId?.[String(contactId)] : null;
      return chatOrderEntry(chat, !!contact?.chatPreferences?.isPinned);
    });
    return shortCodesRowIndex(entries, shortCodeList[0]?.latestReceivedAt, hasMore);
  }, [showShortCodesItem, sortedChats, chats, chatsContacts, shortCodeList, hasMore]);

  const chatItems = useMemo(
    () => {
      const items = sortedChats.map((chatKey) => (
        <ChatItem
          key={chatKey}
          chat={chats.byKey[chatKey]}
          onClick={handleClick}
          filterOptions={currentFilterOptions}
        />
      ));
      if (shortCodesIndex !== -1) {
        items.splice(shortCodesIndex, 0, <ShortCodesChatItem key="short-codes" />);
      }
      return items;
    },
    [
      sortedChats,
      chats,
      handleClick,
      currentFilterOptions,
      shortCodesIndex,
    ]
  );

  return (
    <PerfectScrollbarTable
      options={{ suppressScrollX: true }}
    >
      <Box
        ref={listBoxRef}
        sx={{ padding: '0 16px' }}
      >
        <List
          subheader={(
            <ListSubheader
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingX: 0,
                paddingBottom: '12px',
                zIndex: 10,
                alignItems: 'center',
              }}
            >
              <ChatListFilter
                value={selectedFilterOption}
                options={filterOptions}
                onChange={handleChatListFilterChange}
              />
              <ChatRefresh
                onClearSearch={onClearSearch}
                filterOptions={getFilterOptionsForApi(selectedFilterOption)}
              />
            </ListSubheader>
          )}
        >
          {(isChatsLoading || isSearching) && sortedChats.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!isChatsLoading && !isSearching && helpers.isEmpty(sortedChats) && (
            <Typography
              color="textSecondary"
              sx={{
                padding: '12px',
                textAlign: 'center',
              }}
            >
              {searchText ? 'No connections found matching your search' : 'No connections found'}
            </Typography>
          )}

          {chatItems}

          <Box
            ref={loadMoreRef}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 2,
              minHeight: '60px',
            }}
          >
            {isChatsLoadingMore && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="textSecondary">
                  Loading more chats...
                </Typography>
              </Box>
            )}

            {!hasMore && sortedChats.length > 0 && !isChatsLoadingMore && selectedFilterOption === FILTER_TYPES.all && (
              <Typography variant="body2" color="textSecondary">
                {searchText
                  ? `Found ${totalChats} matching connection${totalChats !== 1 ? 's' : ''}`
                  : `All chats loaded (${totalChats} total)`}
              </Typography>
            )}

            {selectedFilterOption !== FILTER_TYPES.all && sortedChats.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                Showing
                {' '}
                {realUnreadMessagesCount === sortedChats.length ? `${sortedChats.length} unread of` : `${sortedChats.length} of`}
                {' '}
                {totalUnread}
                {' '}
                loaded chats
                {realUnreadMessagesCount !== sortedChats.length && ` (${realUnreadMessagesCount} unread)`}
              </Typography>
            )}
          </Box>
        </List>
      </Box>
    </PerfectScrollbarTable>
  );
}

ChatList.propTypes = {
  searchText: PropTypes.string.isRequired,
  onClearSearch: PropTypes.func.isRequired,
  isHidden: PropTypes.bool,
};

export default ChatList;
